/**
 * Home dashboard → Owners Hub Stats API mapping.
 *
 * Three concerns, all data-driven so `useHomeStats` stays declarative:
 *
 *  1. Interval → API window. The UI shows friendly ranges ("Last 7 days"); the
 *     API wants `start_date` / `end_date` (YYYY-MM-DD, **end-exclusive**:
 *     `start_date <= row < end_date`) plus an `interval_slice` for the history
 *     buckets. {@link homeWindow} is the single source of truth for that.
 *
 *  2. KPI / chart-series → data-set fields. The data set splits every money
 *     metric into `casino_*` and `sport_*`, so a "total" KPI sums BOTH (decision:
 *     casino + sport). Counts (`registrations`, `ftds`) are single fields.
 *
 *  3. Period-over-period deltas. The API has no comparison field, so we fetch a
 *     baseline window (the selected window shifted back by the section's
 *     comparison unit: 7 days for Acquisition, 30 days for Financial / Money
 *     Flow) and compute the % change ourselves in `useHomeStats`. The shift is
 *     a fixed unit per section; the window it shifts is whatever the selector
 *     picked, so the delta is "dynamic" w.r.t. the range.
 *
 * Verified data set: `daily_client_activity` (no dimensions → flat metrics). See
 * `docs/api.owners-hub.stats.md` and `pages/home/missing-data.md`.
 */
import type { IconName } from '../../components/Icon/Icon';
import type { HomeCustomRange, HomeInterval, HomePeriod } from '../../mock-data/types';
import { dateLocale } from '../../utils/locale';
import type { IntervalOption } from './components';

/**
 * The intervals shown in the range selector — the single source of truth shared by
 * the Home Key Metrics header and the Stats page's Brand-metrics header (so the two
 * pickers stay in lockstep). `label` is the human-friendly copy the dropdown
 * renders; `value` is the internal code. The technical interval strings the API
 * receives live in {@link HOME_INTERVAL_API}, keyed by these `value`s.
 */
export const HOME_INTERVAL_OPTIONS: IntervalOption<HomeInterval>[] = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: '7D', label: 'Last 7 days' },
  { value: '30D', label: 'Last 30 days' },
  { value: '90D', label: 'Last 90 days' },
  { value: '6M', label: 'Last 6 months' },
  { value: '1Y', label: 'Last year' },
];

/**
 * Amount fields come back as scaled integers (e.g. `sport_bet_amount: 6161716400`
 * ≈ $61.62). Divide by this to get the major currency unit. Factor confirmed with
 * backend: 100,000,000 (1e8).
 */
export const AMOUNT_SCALE = 100_000_000;

/** How one Home KPI is sourced from (and displayed off) the data-set fields. */
export type HomeKpiSpec = {
  /** Bucket fields summed into the figure (casino_* + sport_* for money totals). */
  fields: readonly string[];
  /** Money prefix shown before the figure (e.g. `$`). */
  prefix?: string;
  /** Suffix shown after the figure (e.g. `K`). */
  suffix?: string;
  /** Multiply the raw aggregate before display (scaled → $, or scaled → $K). The
   *  delta is computed on RAW sums, so `scale` never affects it. */
  scale?: number;
  /** The API exposes no field for this KPI — render a `?` placeholder and keep it
   *  out of the request (see Player Balance). */
  missing?: boolean;
};

/**
 * How the x-axis / tooltip labels are derived from each bucket's `date`. The
 * `hour*` kinds localize the UTC timestamp to the browser's timezone (intra-day
 * times are clearest in local clock); the day/week/month kinds keep the bucket's
 * UTC calendar date (the data is aggregated on UTC day boundaries, so shifting a
 * UTC-midnight stamp into another zone would mislabel the day).
 */
export type CohortLabel = 'hour' | 'hourDated' | 'weekday' | 'weekOf' | 'dayOf' | 'month';

/** Technical window params for one UI range. */
export type StatsIntervalParams = {
  /** Bucket size sent to the API, e.g. `'1 day'`, `'1 week'`. */
  intervalSlice: string;
  /** Length of the selected window in days (drives `start_date`). */
  spanDays: number;
  /** Days the window ends BEFORE local-tomorrow 00:00: `0` includes the live current
   *  day (Today / rolling ranges); `1` ends at local today 00:00 (Yesterday = the
   *  last full local day). */
  endOffsetDays: number;
  /** How to format the chart's cohort labels from the bucket dates. */
  label: CohortLabel;
};

/**
 * UI range → API window/slice + how its chart cohorts are labelled. The slice is the
 * cohort granularity the chart draws (7D & 30D daily, 90D/6M/1Y monthly; 30D shows the
 * daily points behind a sparse ~weekly axis — see `dayOf`). Labels are derived from the
 * REAL bucket dates the API returns (so the ticks read "Jun 1", not "W1").
 *
 * RisingWave's most atomic aggregate is ONE DAY (each row stamped at 00:00Z) — there
 * are NO hourly rows — so Today/Yesterday slice by `1 day` too. (A sub-daily slice
 * just returns one populated bucket at 00:00Z and 23 empty ones, which reads as
 * "daily" anyway.) Those two ranges don't draw a line chart: they render a prior-day
 * vs current-day DayBars comparison (see `AcquisitionMetric`/`FinancialPerformance`),
 * so their `label` is inert. Every range whose window includes today (all but
 * Yesterday) carries the live latest-day slice — see {@link intervalIncludesToday}.
 */
export const HOME_INTERVAL_API: Record<HomeInterval, StatsIntervalParams> = {
  // The live UTC day (latest daily slice, re-aggregated ~every 5 min). DayBars view.
  TODAY: { intervalSlice: '1 day', spanDays: 1, endOffsetDays: 0, label: 'weekday' },
  // The last FULL UTC day (ends at today 00:00 UTC). DayBars view.
  YESTERDAY: { intervalSlice: '1 day', spanDays: 1, endOffsetDays: 1, label: 'weekday' },
  '7D': { intervalSlice: '1 day', spanDays: 7, endOffsetDays: 0, label: 'weekday' },
  // Sliced by DAY, not week. `useHomeStats` fetches one extended (current + prior) window and
  // splits it at the current-period start client-side (`splitByStart`); a WEEKLY bucket straddling
  // that boundary is dated by its week-start, so the whole (partial) first week — and any activity
  // in it — was filed into the PRIOR period, leaving the current 30-day window reading zero. Daily
  // buckets align exactly to the boundary, so nothing straddles. The axis is sparse-ticked (see
  // `dayOf`) so ~30 daily points don't crowd it.
  '30D': { intervalSlice: '1 day', spanDays: 30, endOffsetDays: 0, label: 'dayOf' },
  '90D': { intervalSlice: '1 month', spanDays: 90, endOffsetDays: 0, label: 'month' },
  '6M': { intervalSlice: '1 month', spanDays: 182, endOffsetDays: 0, label: 'month' },
  '1Y': { intervalSlice: '1 month', spanDays: 365, endOffsetDays: 0, label: 'month' },
};

/* ---------- custom ranges ---------- */

/** The longest custom range: a year, leap day included (the presets reach a year too). */
export const MAX_CUSTOM_DAYS = 366;
/** The shortest custom range. The data is daily, so a single day has no trend line to draw (Today and Yesterday have
 *  their own day-bars view). */
export const MIN_CUSTOM_DAYS = 2;

export const isHomeInterval = (period: string): period is HomeInterval =>
  HOME_INTERVAL_OPTIONS.some((option) => option.value === period);

export const customPeriod = (from: string, to: string): HomeCustomRange => `${from}..${to}`;

/**
 * A custom period's first and last day (`YYYY-MM-DD`, the viewer's calendar dates) and how many days it covers, or null
 * when it is not a valid one (days in order, {@link MIN_CUSTOM_DAYS} to {@link MAX_CUSTOM_DAYS} of them). Pure.
 */
export const customDays = (period: string): { from: string; to: string; days: number } | null => {
  const m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(period);
  if (!m) return null;
  const first = Date.parse(`${m[1]}T00:00:00Z`);
  const last = Date.parse(`${m[2]}T00:00:00Z`);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;
  const days = Math.round((last - first) / MS_DAY) + 1;
  return days >= MIN_CUSTOM_DAYS && days <= MAX_CUSTOM_DAYS ? { from: m[1], to: m[2], days } : null;
};

/**
 * A period's window params. A preset's come from {@link HOME_INTERVAL_API}. A custom range is always sliced by DAY,
 * whatever its length: `useHomeStats` splits one extended (prior + current) read at the current period's first day, and
 * only daily buckets line up with that split (see the 30D note above); its axis ticks thin out as it grows (`dayOf`).
 * `endOffsetDays` counts back from the viewer's today, so a range ending today reads live. A custom value that is not a
 * valid range reads as the last 7 days.
 */
export const periodParams = (period: HomePeriod, nowMs: number = Date.now()): StatsIntervalParams => {
  if (isHomeInterval(period)) return HOME_INTERVAL_API[period];
  const range = customDays(period);
  if (!range) return HOME_INTERVAL_API['7D'];
  const today = Date.parse(`${localDateYMD(nowMs, 0)}T00:00:00Z`);
  const endOffsetDays = Math.max(0, Math.round((today - Date.parse(`${range.to}T00:00:00Z`)) / MS_DAY));
  return { intervalSlice: '1 day', spanDays: range.days, endOffsetDays, label: range.days <= 7 ? 'weekday' : 'dayOf' };
};

/** The viewer's today, `YYYY-MM-DD` (the last day a custom range can end on). */
export const localToday = (nowMs: number = Date.now()): string => localDateYMD(nowMs, 0);

/**
 * Whether a range's window includes today's daily slice — true for every range except
 * Yesterday (and a custom range that ends before today). RisingWave re-aggregates the
 * latest daily slice (today) ~every 5 minutes, so ANY window reaching today carries live
 * data and is re-polled on that cadence (not just the Today range). Yesterday ends at
 * today 00:00Z, so its data is already final.
 */
export const intervalIncludesToday = (interval: HomePeriod): boolean => periodParams(interval).endOffsetDays === 0;

/**
 * Each wired Home section → its stats data set, the KPIs it reads and (where it
 * draws a chart) the series fields + chart-display scale. Every metric is a `sum`
 * over the window.
 *
 * All three sections read the SAME data set (`daily_client_activity`), so
 * `useHomeStats` issues ONE (extended) history request for the whole dashboard.
 * The delta compares the selected window against the equal period immediately
 * before it (see {@link COMPARISON_LABEL}); the comparison is the same for every
 * section and scales with the range, so there is no per-section comparison unit.
 */
export const HOME_STATS = {
  acquisition: {
    dataSet: 'daily_client_activity',
    kpis: {
      registrations: { fields: ['registrations'] },
      ftds: { fields: ['ftds'] },
    },
    series: { registrations: ['registrations'], ftds: ['ftds'] },
    /** Counts — plotted as-is. */
    chartScale: 1,
  },
  financial: {
    dataSet: 'daily_client_activity',
    kpis: {
      grossRevenue: { fields: ['casino_ggr', 'sport_ggr'], prefix: '$', scale: 1 / AMOUNT_SCALE },
      // Stakes / Wins use the SAME whole-currency scale as Gross Revenue above and the
      // Stats page (`1 / AMOUNT_SCALE`). They previously divided by an extra 1000 with a
      // literal `K` suffix, but `StatValue` rounds to a whole integer (no abbreviation),
      // so every value under ~$500 collapsed to `$0K`. See the chart's `chartScale` below.
      stakes: { fields: ['casino_bet_amount', 'sport_bet_amount'], prefix: '$', scale: 1 / AMOUNT_SCALE },
      wins: { fields: ['casino_win_amount', 'sport_win_amount'], prefix: '$', scale: 1 / AMOUNT_SCALE },
    },
    series: {
      ggr: ['casino_ggr', 'sport_ggr'],
      stakes: ['casino_bet_amount', 'sport_bet_amount'],
      wins: ['casino_win_amount', 'sport_win_amount'],
    },
    /** Plot in whole currency so the `$…k` axis formatter reads naturally. */
    chartScale: 1 / AMOUNT_SCALE,
  },
  moneyFlow: {
    dataSet: 'daily_client_activity',
    kpis: {
      deposits: { fields: ['deposit_amount'], prefix: '$', scale: 1 / AMOUNT_SCALE },
      // Whole-currency scale, matching Deposits above and the Stats page (same `$0K`
      // rounding bug as Stakes / Wins before this).
      withdrawals: { fields: ['withdraw_processed_amount'], prefix: '$', scale: 1 / AMOUNT_SCALE },
      // No balance metric exists in the data set — render `?` (kept mock-shaped so
      // it flips to live the moment a balance field lands). See missing-data.md.
      playerBalance: { fields: [], prefix: '$', missing: true },
    },
  },
} as const;

/**
 * The delta caption per range — the "vs …" label under each figure. The delta
 * compares the selected window to the **equal period immediately before it**, so
 * the caption names that prior period and adapts to whatever the selector picks
 * (it is NOT a fixed per-section "vs last week" / "vs last month").
 */
export const COMPARISON_LABEL: Record<HomeInterval, string> = {
  TODAY: 'vs yesterday',
  YESTERDAY: 'vs previous day',
  '7D': 'vs previous 7 days',
  '30D': 'vs previous 30 days',
  '90D': 'vs previous 90 days',
  '6M': 'vs previous 6 months',
  '1Y': 'vs previous year',
};

/* ---------- Acquisition Channels (affiliate-type breakdown) ---------- */

/**
 * `aft` (affiliate type) literals — convened with the other systems that populate
 * the data. Each Acquisition Channel card queries `daily_affiliate_activity` grouped
 * on `aft` and filtered to one of these values (`filter: { aft }`), then matches the
 * echoed `aggregate_on.aft` in the response. Documented in `docs/api.owners-hub.stats.md`.
 */
export type AffiliateType = 'web' | 'app' | 'crypto' | 'video' | 'affiliates' | 'influencers';

export type HomeChannelConfig = {
  /** Card id — also the drill-in route key (see `CHANNEL_ROUTE` in `index.tsx`). */
  id: string;
  name: string;
  icon: IconName;
  /** The `aft` literal sent as `filter: { aft }` and matched in the response. */
  aft: AffiliateType;
};

/** The six Acquisition Channel cards, in display order. */
export const HOME_CHANNELS: readonly HomeChannelConfig[] = [
  { id: 'web', name: 'Web', icon: 'globe', aft: 'web' },
  { id: 'apps', name: 'Apps', icon: 'mobile', aft: 'app' },
  { id: 'crypto', name: 'Crypto', icon: 'crypto', aft: 'crypto' },
  { id: 'short', name: 'Short Videos', icon: 'video', aft: 'video' },
  { id: 'affiliates', name: 'Affiliates', icon: 'affiliates', aft: 'affiliates' },
  { id: 'influencers', name: 'Influencers', icon: 'influencers', aft: 'influencers' },
];

/**
 * The cards Home shows by default: Affiliates alone. The plain "777" reveal (`show_in_progress`) shows every card in
 * {@link HOME_CHANNELS}. A separate list, not a trim of `HOME_CHANNELS`: the Acquisition Performance totals sum every
 * `aft` there (`ACQUISITION_AFTS`).
 */
export const HOME_DEFAULT_CHANNEL_IDS: readonly string[] = ['affiliates'];

/**
 * How the channel cards are sourced from `daily_affiliate_activity`. Each card makes
 * its OWN history call(s) per window, grouped on `aft` AND filtered to the card's
 * `aft` literal (`aggregate_on: ["aft"]`, `filter: { aft }`), so cards fetch
 * independently (one erroring degrades alone). The backend rejects an object-form
 * `aggregate_on` (must be the string array `["aft"]`), so the value goes in `filter`;
 * the response is read by matching the echoed `aggregate_on.aft` to the card (see
 * `toBucketsFor`), robust whether the server narrows to the filtered group or echoes
 * several. `aggregateOn` + `filter` are both part of the cache-key identity, so the
 * per-card calls don't clobber one another.
 */
export const HOME_CHANNEL_STATS = {
  dataSet: 'daily_affiliate_activity',
  aggregateOn: ['aft'],
  fields: {
    registrations: ['registrations'],
    ftds: ['ftds'],
    /** FTD value (money, scaled integer). */
    ftdValue: ['ftd_total_amount'],
  },
  /** Plot FTD value in whole currency so it sits near the registration/FTD counts. */
  ftdValueChartScale: 1 / AMOUNT_SCALE,
} as const;

/* ---------- window maths ----------
 *
 * The live aggregation runs in SERVER (UTC) time, and the backend serves
 * 5-minute-fresh data ONLY for the current UTC day; any query that reaches before
 * today 00:00 UTC (or spans > ~24h) falls back to the collapsed pre-calculation and
 * LOSES the live updates. So:
 *  - TODAY: the current UTC day, `[today 00:00 UTC, tomorrow 00:00 UTC]`. The server
 *    caps the end at "now"; the window is a stable key so the 5-minute refresh
 *    updates in place.
 *  - YESTERDAY: the last full UTC day, `[yesterday 00:00 UTC, today 00:00 UTC]`
 *    (complete, already collapsed).
 *  - day / week / month ranges: LOCAL calendar dates (`YYYY-MM-DD`); they are
 *    already > 24h so they read the collapsed path regardless, and the bucket date
 *    numbers line up with the viewer's local dates.
 *
 * The delta NEVER extends the current window — each consumer issues a SEPARATE
 * request for the prior equal period (`shiftWindow(window, spanDays)`), so the
 * current "Today" call stays inside the live window.
 */

const MS_DAY = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (n: number): string => String(n).padStart(2, '0');

/** Start of the UTC day containing `ms`. */
const utcDayStartMs = (ms: number): number => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};
const isoOf = (ms: number): string => new Date(ms).toISOString();
/** Local calendar date `YYYY-MM-DD`, `dayOffset` days from the day containing `nowMs`. */
const localDateYMD = (nowMs: number, dayOffset: number): string => {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + dayOffset);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export type Window = { startDate: string; endDate: string };

/**
 * The selected window for a range. TODAY / YESTERDAY are UTC-day windows (server
 * time, where the live aggregation lives — see the block comment); the multi-day
 * ranges are the viewer's local calendar dates. End-exclusive.
 */
export const homeWindow = (interval: HomePeriod, nowMs: number): StatsIntervalParams & Window => {
  const params = periodParams(interval, nowMs);
  // A custom range is fixed dates: its window never moves with the clock (only its live flag does).
  const range = isHomeInterval(interval) ? null : customDays(interval);
  if (range) {
    const dayAfter = new Date(Date.parse(`${range.to}T00:00:00Z`) + MS_DAY).toISOString().slice(0, 10);
    return { ...params, startDate: range.from, endDate: dayAfter };
  }
  if (interval === 'TODAY') {
    const day0 = utcDayStartMs(nowMs);
    return { ...params, startDate: isoOf(day0), endDate: isoOf(day0 + MS_DAY) };
  }
  if (interval === 'YESTERDAY') {
    const day0 = utcDayStartMs(nowMs);
    return { ...params, startDate: isoOf(day0 - MS_DAY), endDate: isoOf(day0) };
  }
  const endOff = 1 - params.endOffsetDays;
  const startOff = endOff - params.spanDays;
  return { ...params, startDate: localDateYMD(nowMs, startOff), endDate: localDateYMD(nowMs, endOff) };
};

/** The window shifted back `days` days — the prior equal period for the delta.
 *  Preserves the window's format (ISO datetime vs `YYYY-MM-DD`). */
export const shiftWindow = (window: Window, days: number): Window => {
  const dateOnly = window.startDate.length === 10;
  const shift = (s: string): string => {
    const ms = Date.parse(s) - days * MS_DAY;
    return dateOnly ? new Date(ms).toISOString().slice(0, 10) : new Date(ms).toISOString();
  };
  return { startDate: shift(window.startDate), endDate: shift(window.endDate) };
};

/* ---------- cohort labels from real bucket dates ---------- */

// Intra-day labels — SERVER (UTC) time, because the hourly aggregation is bucketed
// in UTC and the live "Today" window is the UTC day. A Today bucket reads "00:00"
// at 00:00 UTC, so the axis aligns to 00:00 server time.
const utcHour2 = (ms: number): string => pad(new Date(ms).getUTCHours());
const utcHourLabel = (ms: number): string => `${pad(new Date(ms).getUTCHours())}:00`;
const utcHourDatedLabel = (ms: number): string => {
  const d = new Date(ms);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:00`;
};

// Day / week / month labels — kept on the bucket's UTC calendar date (the data is
// aggregated on UTC day boundaries, so these are dates, not localizable instants).
const weekdayLabel = (ms: number): string => WEEKDAYS[new Date(ms).getUTCDay()];
const monthLabel = (ms: number): string => MONTHS[new Date(ms).getUTCMonth()];
// Weekly axis tick = the week's start date, no "Week of" prefix (it crowded and is
// redundant on a weekly chart). Matches the tooltip, which is the same date + year.
const weekStartLabel = (ms: number): string => `${MONTHS[new Date(ms).getUTCMonth()]} ${new Date(ms).getUTCDate()}`;

// Year-stamped variants for the tooltip headings (the visible axis ticks stay
// compact; the tooltip carries the full, unambiguous date — see `cohortLabels`).
const weekdayDateLabel = (ms: number): string => {
  const d = new Date(ms);
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};
// Tooltip for the weekly view: just the (year-stamped) date — no "Week of" prefix
// (that stays on the axis tick).
const weekOfYearLabel = (ms: number): string => {
  const d = new Date(ms);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};
const monthYearLabel = (ms: number): string => `${monthLabel(ms)} ${new Date(ms).getUTCFullYear()}`;

/**
 * Build the chart's `xLabels` (visible axis ticks) and `pointLabels` (per-sample
 * tooltip headings) from the buckets' own `date`s. Axis ticks stay compact (sparse
 * UTC hours, a weekday, "Jun 1" for weeks, "Jun" for months); the tooltip's
 * `pointLabels` carry the full date INCLUDING the year so the hover readout is
 * unambiguous.
 *
 *  - `hour` (Today): UTC time of day, no date (it is the current day).
 *  - `hourDated` (Yesterday): same hourly axis, but each slice's tooltip carries
 *    the (UTC) date too, since the day is not "now".
 *  - day/week/month: UTC calendar dates (see {@link CohortLabel}).
 */
export const cohortLabels = (
  interval: HomePeriod,
  bucketDates: string[],
): { xLabels: string[]; pointLabels: string[] } => {
  const kind = periodParams(interval).label;
  const ms = bucketDates.map((d) => Date.parse(d));
  // Hourly axes are SERVER time — mark the leading tick "UTC" and tag every tooltip,
  // so the times can't be misread as the viewer's local clock.
  const sparseHours = (): string[] =>
    ms.filter((_, i) => i % 4 === 0).map((m, i) => (i === 0 ? `${utcHour2(m)} UTC` : utcHour2(m)));
  switch (kind) {
    case 'hour':
      return { xLabels: sparseHours(), pointLabels: ms.map((m) => `${utcHourLabel(m)} UTC`) };
    case 'hourDated':
      return { xLabels: sparseHours(), pointLabels: ms.map((m) => `${utcHourDatedLabel(m)} UTC`) };
    case 'weekday':
      return { xLabels: ms.map(weekdayLabel), pointLabels: ms.map(weekdayDateLabel) };
    case 'weekOf':
      return { xLabels: ms.map(weekStartLabel), pointLabels: ms.map(weekOfYearLabel) };
    case 'dayOf': {
      // Daily buckets over ~a month: one tick per day crowds the axis, so sparse the VISIBLE ticks
      // to about one a week (at most eight on a long custom range, e.g. "Aug 12") while the tooltip
      // still names each day (year-stamped). The chart spaces `xLabels` evenly from the first point
      // to the last, so the ticks are picked the same way (first and last point included) and each
      // names the point it sits under. Reuses the generic date formatters.
      const n = ms.length;
      const k = Math.min(8, Math.ceil(n / 7));
      const ticks = k <= 1 ? ms.slice(0, 1) : Array.from({ length: k }, (_, j) => ms[Math.round((j * (n - 1)) / (k - 1))]);
      return { xLabels: ticks.map(weekStartLabel), pointLabels: ms.map(weekOfYearLabel) };
    }
    case 'month':
      return { xLabels: ms.map(monthLabel), pointLabels: ms.map(monthYearLabel) };
  }
};

/**
 * A run of whole days in words ("Sep 1" … "Sep 30"), the year only when either end is not in this year. `first` and
 * `last` are the first and last day's UTC midnights (calendar dates, so they are formatted in UTC). Shared with the
 * Website tab's custom ranges. Pure but for the clock's year.
 */
export const dayRunWords = (first: number, last: number, language: string): { from: string; to: string } => {
  const thisYear = new Date().getUTCFullYear();
  const withYear = new Date(first).getUTCFullYear() !== thisYear || new Date(last).getUTCFullYear() !== thisYear;
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(withYear ? { year: 'numeric' } : {}),
  };
  // The app's language codes are folder names (`mx`, `bra`), not locales: Intl needs the mapped tag.
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat(dateLocale(language), opts);
  } catch {
    fmt = new Intl.DateTimeFormat(undefined, opts);
  }
  return { from: fmt.format(first), to: fmt.format(last) };
};

/** A custom period as the picker's pill shows it ("Sep 1 – Sep 30"), or null when it is not a valid one. */
export const customPeriodLabel = (period: string, language: string): string | null => {
  const range = customDays(period);
  if (!range) return null;
  const words = dayRunWords(Date.parse(`${range.from}T00:00:00Z`), Date.parse(`${range.to}T00:00:00Z`), language);
  return `${words.from} – ${words.to}`;
};
